export const CAPABILITY_TYPES = [
  { value: 'physical', label: 'Physical' },
  { value: 'digital', label: 'Digital' },
  { value: 'hybrid', label: 'Hybrid' },
];

export const CAPABILITY_STATUSES = [
  { value: 'draft', label: 'Draft' },
  { value: 'active', label: 'Active' },
  { value: 'inactive', label: 'Inactive' },
  { value: 'retired', label: 'Retired' },
];

export const CAPABILITY_VISIBILITIES = [
  { value: 'business_unit', label: 'Business Unit' },
  { value: 'partner', label: 'Partner' },
  { value: 'federation', label: 'Federation' },
];

export const ACCEPTANCE_MODES = [
  { value: 'manual', label: 'Manual' },
  { value: 'automatic', label: 'Automatic' },
];

export const FULFILLMENT_MODES = [
  { value: 'manual', label: 'Manual' },
  { value: 'automated', label: 'Automated' },
  { value: 'hybrid', label: 'Hybrid' },
];

export const ADDITIONAL_INPUT_TYPES = [
  { value: 'text', label: 'Text' },
  { value: 'number', label: 'Number' },
  { value: 'boolean', label: 'Boolean' },
  { value: 'selection', label: 'Selection' },
  { value: 'file', label: 'File' },
  { value: 'structured_data', label: 'Structured Data' },
  { value: 'physical_item', label: 'Physical Item' },
  { value: 'reference', label: 'Reference' },
];

export const REQUEST_FIELD_TIMING_OPTIONS = [
  { value: 'request', label: 'Required when request is submitted' },
  { value: 'before_fulfillment', label: 'Required before work can begin' },
  { value: 'optional', label: 'Optional' },
];

export const REQUIREMENT_STAGES = REQUEST_FIELD_TIMING_OPTIONS;

export const OUTPUT_KINDS = [
  { value: 'file', label: 'File' },
  { value: 'structured_data', label: 'Structured Data' },
  { value: 'physical_item', label: 'Physical Item' },
  { value: 'value', label: 'Value' },
];

export const MATCH_MODES = [
  { value: 'numeric_range', label: 'Range' },
  { value: 'allowed_values', label: 'Allowed Values' },
  { value: 'boolean_support', label: 'Boolean Support' },
  { value: 'exact_value', label: 'Exact Value' },
];

const MATCH_MODE_ALIASES = {
  range: 'numeric_range',
  min: 'numeric_range',
  max: 'numeric_range',
  min_max: 'numeric_range',
  minmax: 'numeric_range',
  selection: 'allowed_values',
  allowed_selection_values: 'allowed_values',
  supported_values: 'allowed_values',
  enum: 'exact_value',
  enumerated: 'exact_value',
  exact: 'exact_value',
  boolean: 'boolean_support',
};

export function formatCapabilityLabel(value) {
  return String(value || '')
    .replace(/_/g, ' ')
    .replace(/\b\w/g, (char) => char.toUpperCase());
}

export function normalizeObject(value) {
  return value && typeof value === 'object' && !Array.isArray(value) ? value : {};
}

export function slugifyCapabilityTitle(value) {
  return String(value || '')
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    || `capability-${Date.now()}`;
}

export function schemaKeyFromLabel(value, fallbackPrefix = 'field') {
  const normalizedPrefix = String(fallbackPrefix || 'field')
    .toLowerCase()
    .replace(/[^a-z0-9_]+/g, '_')
    .replace(/^_+|_+$/g, '') || 'field';
  const key = String(value || '')
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '_')
    .replace(/^_+|_+$/g, '');
  const base = key || normalizedPrefix;
  return /^[a-z_]/.test(base) ? base : `${normalizedPrefix}_${base}`;
}

export function uniqueSchemaKey(value, existingKeys = [], fallbackPrefix = 'field') {
  const used = new Set((existingKeys || []).map((key) => String(key || '').trim()).filter(Boolean));
  const base = schemaKeyFromLabel(value, fallbackPrefix);
  let candidate = base;
  let suffix = 2;
  while (used.has(candidate)) {
    candidate = `${base}_${suffix}`;
    suffix += 1;
  }
  return candidate;
}

export function isValidContractKey(value) {
  return /^[A-Za-z_][A-Za-z0-9_-]*$/.test(String(value || '').trim());
}

function schemaTypeForInputKind(inputKind) {
  if (inputKind === 'number') {
    return 'number';
  }
  if (inputKind === 'boolean') {
    return 'boolean';
  }
  if (['structured_data', 'physical_item', 'reference'].includes(inputKind)) {
    return 'object';
  }
  return 'string';
}

function fieldLabel(value, fallback) {
  const text = String(value || '').trim();
  return text || formatCapabilityLabel(fallback);
}

function normalizeFieldType(value) {
  const type = String(value || 'text').trim().toLowerCase();
  if (type === 'string') {
    return 'text';
  }
  if (type === 'array') {
    return 'selection';
  }
  if (type === 'integer') {
    return 'number';
  }
  return type;
}

function metadataValue(source, ...keys) {
  const metadata = normalizeObject(source?.metadata);
  const nestedMatching = normalizeObject(source?.['x-amx']?.matching);
  for (const key of keys) {
    if (Object.prototype.hasOwnProperty.call(source || {}, key)) {
      return source[key];
    }
    if (Object.prototype.hasOwnProperty.call(metadata, key)) {
      return metadata[key];
    }
    if (Object.prototype.hasOwnProperty.call(nestedMatching, key)) {
      return nestedMatching[key];
    }
  }
  return undefined;
}

function metadataBool(source, ...keys) {
  const value = metadataValue(source, ...keys);
  if (typeof value === 'boolean') {
    return value;
  }
  if (value === null || typeof value === 'undefined') {
    return undefined;
  }
  const normalized = String(value).trim().toLowerCase();
  if (['true', '1', 'yes', 'y', 'on'].includes(normalized)) {
    return true;
  }
  if (['false', '0', 'no', 'n', 'off'].includes(normalized)) {
    return false;
  }
  return undefined;
}

function normalizeMatchMode(value) {
  const normalized = String(value || '').trim().toLowerCase().replace(/[-/\s]+/g, '_');
  return MATCH_MODE_ALIASES[normalized] || normalized;
}

function normalizeOptions(options) {
  return (Array.isArray(options) ? options : [])
    .map((option) => {
      if (option && typeof option === 'object') {
        const value = String(option.value ?? option.key ?? option.label ?? '').trim();
        return value ? { value, label: String(option.label ?? option.value ?? option.key ?? value) } : null;
      }
      const value = String(option ?? '').trim();
      return value ? { value, label: value } : null;
    })
    .filter(Boolean);
}

function optionsFromJsonSchemaProperty(propertySchema) {
  const directOptions = normalizeOptions(propertySchema?.enum);
  if (directOptions.length) {
    return directOptions;
  }
  return normalizeOptions(propertySchema?.items?.enum);
}

function parseListValue(value) {
  if (Array.isArray(value)) {
    return value
      .map((item) => (item && typeof item === 'object' ? item.value ?? item.key ?? item.label : item))
      .map((item) => String(item ?? '').trim())
      .filter(Boolean);
  }
  return String(value || '')
    .split(/[\n,]/)
    .map((item) => item.trim())
    .filter(Boolean);
}

function normalizeOptionalNumber(value, fieldName) {
  if (value === null || typeof value === 'undefined' || value === '') {
    return undefined;
  }
  const normalized = Number(value);
  if (Number.isNaN(normalized)) {
    throw new Error(`${fieldName} must be a number.`);
  }
  return normalized;
}

function inferMatchMode(fieldType, options) {
  if (['number', 'range', 'integer'].includes(fieldType)) {
    return 'numeric_range';
  }
  if (['select', 'selection', 'multiselect', 'tags'].includes(fieldType) && options.length) {
    return 'allowed_values';
  }
  if (fieldType === 'boolean') {
    return 'boolean_support';
  }
  if (options.length) {
    return 'exact_value';
  }
  return '';
}

function matchingMetadataForField(source, fieldType, options) {
  const configuredMode = metadataValue(
    source,
    'x-amx-match-mode',
    'x_amx_match_mode',
    'match_mode',
    'mode',
  );
  let matchMode = configuredMode ? normalizeMatchMode(configuredMode) : '';
  let matchable = metadataBool(
    source,
    'x-amx-matchable',
    'x_amx_matchable',
    'matchable',
    'enabled',
  );
  if (typeof matchable === 'undefined') {
    matchable = Boolean(matchMode);
  }
  if (matchable && !matchMode) {
    matchMode = inferMatchMode(fieldType, options);
  }
  if (!MATCH_MODES.some((option) => option.value === matchMode)) {
    matchMode = '';
  }
  return {
    matchable: Boolean(matchable && matchMode),
    match_mode: matchable ? matchMode : '',
  };
}

export function fieldsFromSharedSchema(schema) {
  if (!schema || typeof schema !== 'object') {
    return [];
  }

  const rawFields = Array.isArray(schema.body)
    ? schema.body
    : Array.isArray(schema.fields)
      ? schema.fields
      : null;

  if (rawFields) {
    return rawFields
      .filter((field) => field && typeof field === 'object' && field.name)
      .map((field, index) => {
        const type = normalizeFieldType(field.type);
        const options = normalizeOptions(field.options);
        const matchingMetadata = matchingMetadataForField(field, type, options);
        const acceptedContentTypes = parseListValue(
          field.accepted_content_types || field.accepted_file_types || field.accept,
        );
        return {
          key: String(field.name),
          name: String(field.name),
          display_name: fieldLabel(field.label, field.name),
          description: field.description || '',
          type,
          schema_type: String(field.type || 'text').trim().toLowerCase(),
          required: Boolean(field.required),
          read_only: true,
          source: 'need_category',
          display_order: field.display_order ?? field.position ?? index,
          options,
          accepted_content_types: acceptedContentTypes,
          accepted_file_types: acceptedContentTypes.join(', '),
          multiple: Boolean(field.multiple),
          matchable: matchingMetadata.matchable,
          match_mode: matchingMetadata.match_mode,
        };
      });
  }

  const properties = normalizeObject(schema.properties);
  const required = new Set(Array.isArray(schema.required) ? schema.required : []);
  return Object.entries(properties).map(([key, propertySchema], index) => {
    const normalizedProperty = normalizeObject(propertySchema);
    const type = normalizeFieldType(normalizedProperty['x-amx-input-kind'] || normalizedProperty.type);
    const options = optionsFromJsonSchemaProperty(normalizedProperty);
    const matchingMetadata = matchingMetadataForField(normalizedProperty, type, options);
    const acceptedContentTypes = parseListValue(normalizedProperty['x-amx-accepted-content-types']);
    return {
      key,
      name: key,
      display_name: fieldLabel(normalizedProperty.title, key),
      description: normalizedProperty.description || '',
      type,
      schema_type: String(normalizedProperty['x-amx-input-kind'] || normalizedProperty.type || 'text').trim().toLowerCase(),
      required: required.has(key),
      read_only: true,
      source: 'need_category',
      display_order: normalizedProperty['x-amx-display-order'] ?? index,
      options,
      accepted_content_types: acceptedContentTypes,
      accepted_file_types: acceptedContentTypes.join(', '),
      multiple: Boolean(normalizedProperty['x-amx-multiple']),
      matchable: matchingMetadata.matchable,
      match_mode: matchingMetadata.match_mode,
    };
  });
}

export function fieldsFromAdditionalRequestSchema(schema) {
  const properties = normalizeObject(schema?.properties);
  const required = new Set(Array.isArray(schema?.required) ? schema.required : []);
  return Object.entries(properties)
    .map(([key, propertySchema], index) => {
      const normalizedProperty = normalizeObject(propertySchema);
      const type = normalizeFieldType(normalizedProperty['x-amx-input-kind'] || normalizedProperty.type);
      const options = optionsFromJsonSchemaProperty(normalizedProperty);
      const acceptedContentTypes = parseListValue(normalizedProperty['x-amx-accepted-content-types']);
      return {
        id: `${key}-${index}`,
        key,
        display_name: fieldLabel(normalizedProperty.title, key),
        description: normalizedProperty.description || '',
        type,
        requirement_stage: normalizedProperty['x-amx-requirement-stage'] || (required.has(key) ? 'request' : 'optional'),
        required: required.has(key),
        display_order: normalizedProperty['x-amx-display-order'] ?? index,
        options: options.map((option) => option.value).join('\n'),
        unit: normalizedProperty['x-amx-unit'] || '',
        min: normalizedProperty.minimum ?? '',
        max: normalizedProperty.maximum ?? '',
        accepted_file_types: acceptedContentTypes.join(', '),
        multiple: Boolean(normalizedProperty['x-amx-multiple']),
        data_schema: normalizeObject(normalizedProperty['x-amx-data-schema']),
        reference_target: normalizedProperty['x-amx-reference-target'] || '',
        physical_item_details: normalizedProperty['x-amx-physical-item-details'] || '',
      };
    })
    .sort((left, right) => Number(left.display_order || 0) - Number(right.display_order || 0));
}

export function additionalRequestSchemaFromInputs(inputs) {
  const properties = {};
  const required = [];

  inputs.forEach((input, index) => {
    const rawKey = String(input.key || '').trim();
    if (!rawKey && !String(input.display_name || input.description || '').trim()) {
      return;
    }
    const key = rawKey || uniqueSchemaKey(input.display_name, Object.keys(properties), 'field');
    if (!isValidContractKey(key)) {
      throw new Error('Input keys must start with a letter or underscore and use only letters, numbers, underscores, or hyphens.');
    }
    if (properties[key]) {
      throw new Error('Input keys must be unique.');
    }

    const inputKind = input.type || 'text';
    const stage = input.requirement_stage || 'request';
    const propertySchema = {
      title: String(input.display_name || '').trim() || formatCapabilityLabel(key),
      description: String(input.description || '').trim(),
      type: schemaTypeForInputKind(inputKind),
      'x-amx-input-kind': inputKind,
      'x-amx-requirement-stage': stage,
      'x-amx-display-order': index,
    };

    if (inputKind === 'selection') {
      const options = parseListValue(input.options);
      if (!options.length) {
        throw new Error(`Selection input "${propertySchema.title}" needs at least one option.`);
      }
      propertySchema.enum = options;
    }

    if (inputKind === 'number') {
      const min = normalizeOptionalNumber(input.min, `${propertySchema.title} minimum`);
      const max = normalizeOptionalNumber(input.max, `${propertySchema.title} maximum`);
      if (typeof min !== 'undefined' && typeof max !== 'undefined' && min > max) {
        throw new Error(`${propertySchema.title} minimum cannot exceed maximum.`);
      }
      if (typeof min !== 'undefined') {
        propertySchema.minimum = min;
      }
      if (typeof max !== 'undefined') {
        propertySchema.maximum = max;
      }
      if (String(input.unit || '').trim()) {
        propertySchema['x-amx-unit'] = String(input.unit || '').trim();
      }
    }

    if (inputKind === 'file') {
      const acceptedTypes = parseListValue(input.accepted_file_types || input.accepted_content_types);
      if (acceptedTypes.length) {
        propertySchema['x-amx-accepted-content-types'] = acceptedTypes;
      }
      propertySchema['x-amx-multiple'] = Boolean(input.multiple);
    }

    if (inputKind === 'structured_data') {
      propertySchema['x-amx-data-schema'] = normalizeObject(input.data_schema);
    }

    if (inputKind === 'physical_item' && String(input.physical_item_details || '').trim()) {
      propertySchema['x-amx-physical-item-details'] = String(input.physical_item_details || '').trim();
    }

    if (inputKind === 'reference' && String(input.reference_target || '').trim()) {
      propertySchema['x-amx-reference-target'] = String(input.reference_target || '').trim();
    }

    properties[key] = propertySchema;
    if (stage !== 'optional') {
      required.push(key);
    }
  });

  return {
    $schema: 'https://json-schema.org/draft/2020-12/schema',
    type: 'object',
    properties,
    required,
  };
}

export function additionalInputsForPayload(inputs) {
  const filteredInputs = (inputs || []).filter((input) => (
    String(
      input.key ||
      input.display_name ||
      input.description ||
      input.options ||
      input.unit ||
      input.min ||
      input.max ||
      input.accepted_file_types ||
      input.reference_target ||
      input.physical_item_details ||
      '',
    ).trim()
  ));
  additionalRequestSchemaFromInputs(filteredInputs);
  const usedKeys = new Set();
  return filteredInputs.map((input, index) => ({
    key: (() => {
      const key = String(input.key || '').trim() || uniqueSchemaKey(input.display_name, Array.from(usedKeys), 'field');
      usedKeys.add(key);
      return key;
    })(),
    name: String(input.display_name || '').trim(),
    description: String(input.description || '').trim(),
    type: input.type || 'text',
    requirement_stage: input.requirement_stage || 'request',
    display_order: index,
    options: input.type === 'selection'
      ? parseListValue(input.options)
      : undefined,
    unit: input.type === 'number' ? String(input.unit || '').trim() || undefined : undefined,
    min: input.type === 'number' ? normalizeOptionalNumber(input.min, `${input.display_name || input.key} minimum`) : undefined,
    max: input.type === 'number' ? normalizeOptionalNumber(input.max, `${input.display_name || input.key} maximum`) : undefined,
    accepted_content_types: input.type === 'file'
      ? parseListValue(input.accepted_file_types || input.accepted_content_types)
      : undefined,
    multiple: input.type === 'file' ? Boolean(input.multiple) : undefined,
    data_schema: input.type === 'structured_data' ? normalizeObject(input.data_schema) : undefined,
    physical_item_details: input.type === 'physical_item'
      ? String(input.physical_item_details || '').trim() || undefined
      : undefined,
    reference_target: input.type === 'reference'
      ? String(input.reference_target || '').trim() || undefined
      : undefined,
  }));
}

export function outputsForPayload(outputs) {
  const seen = new Set();
  return outputs
    .filter((output) => (
      String(output.key || output.name || output.description || output.semantic_type || '').trim()
      || Object.keys(normalizeObject(output.data_schema)).length
    ))
    .map((output, index) => {
      const key = String(output.key || '').trim() || uniqueSchemaKey(output.name, Array.from(seen), 'output');
      if (!isValidContractKey(key)) {
        throw new Error('Output keys must start with a letter or underscore and use only letters, numbers, underscores, or hyphens.');
      }
      if (seen.has(key)) {
        throw new Error('Output keys must be unique.');
      }
      seen.add(key);
      return {
        output_id: output.output_id || undefined,
        key,
        name: String(output.name || '').trim() || formatCapabilityLabel(key),
        description: String(output.description || '').trim() || null,
        kind: output.kind || 'file',
        semantic_type: String(output.semantic_type || '').trim() || null,
        required: Boolean(output.required),
        data_schema: normalizeObject(output.data_schema),
        display_order: index,
      };
    });
}

export function eligibleMatchingFields(fields) {
  return (Array.isArray(fields) ? fields : [])
    .filter((field) => Boolean(field.matchable && field.match_mode))
    .sort((left, right) => Number(left.display_order || 0) - Number(right.display_order || 0));
}

function valuesForCriterionValue(value) {
  if (Array.isArray(value)) {
    return value.map((item) => String(item)).filter(Boolean);
  }
  if (value && typeof value === 'object') {
    const allowedValues = value.allowed_values || value.values || value.value;
    if (Array.isArray(allowedValues)) {
      return allowedValues.map((item) => String(item)).filter(Boolean);
    }
  }
  return value === null || typeof value === 'undefined' || value === '' ? [] : [String(value)];
}

function criterionConfigFromLegacyValue(field, value) {
  if (field.match_mode === 'numeric_range') {
    if (value && typeof value === 'object' && !Array.isArray(value)) {
      return {
        min: value.min ?? value.minimum ?? '',
        max: value.max ?? value.maximum ?? '',
      };
    }
    return { min: value ?? '', max: value ?? '' };
  }
  if (field.match_mode === 'allowed_values') {
    return { allowed_values: valuesForCriterionValue(value) };
  }
  if (field.match_mode === 'boolean_support') {
    if (value === true) {
      return { support: 'yes' };
    }
    if (value === false) {
      return { support: 'no' };
    }
    const normalized = String(value || '').trim().toLowerCase();
    return { support: ['yes', 'no', 'either'].includes(normalized) ? normalized : 'either' };
  }
  if (value && typeof value === 'object' && !Array.isArray(value) && Object.prototype.hasOwnProperty.call(value, 'value')) {
    return { value: value.value };
  }
  return { value: value ?? '' };
}

function criterionFromProfileEntry(entry, field, index) {
  const config = normalizeObject(entry.config);
  return {
    id: `${field.key}-${index}-${Math.random().toString(36).slice(2)}`,
    field_key: field.key,
    match_mode: field.match_mode,
    config: Object.keys(config).length ? config : criterionConfigFromLegacyValue(field, entry.value),
  };
}

export function matchingStateFromProfile(profile, sharedFields) {
  const normalizedProfile = normalizeObject(profile);
  const fieldsByKey = new Map(eligibleMatchingFields(sharedFields).map((field) => [field.key, field]));
  const criteria = [];
  const preservedProfile = { ...normalizedProfile };

  if (Array.isArray(normalizedProfile.criteria)) {
    delete preservedProfile.criteria;
    normalizedProfile.criteria.forEach((criterion, index) => {
      const field = fieldsByKey.get(String(criterion?.field_key || ''));
      if (field) {
        criteria.push(criterionFromProfileEntry(criterion, field, index));
      } else {
        preservedProfile.legacy_criteria = [...(preservedProfile.legacy_criteria || []), criterion];
      }
    });
    return { criteria, preservedProfile };
  }

  Object.entries(normalizedProfile).forEach(([key, value], index) => {
    const field = fieldsByKey.get(key);
    if (field) {
      criteria.push({
        id: `${field.key}-${index}-${Math.random().toString(36).slice(2)}`,
        field_key: field.key,
        match_mode: field.match_mode,
        config: criterionConfigFromLegacyValue(field, value),
      });
      delete preservedProfile[key];
    }
  });

  return { criteria, preservedProfile };
}

function normalizeCriterionConfig(criterion, field) {
  const config = normalizeObject(criterion.config);
  if (field.match_mode === 'numeric_range') {
    const min = config.min ?? '';
    const max = config.max ?? '';
    if (min === '' && max === '') {
      throw new Error(`${field.display_name} needs a minimum or maximum value.`);
    }
    if (min !== '' && Number.isNaN(Number(min))) {
      throw new Error(`${field.display_name} minimum must be a number.`);
    }
    if (max !== '' && Number.isNaN(Number(max))) {
      throw new Error(`${field.display_name} maximum must be a number.`);
    }
    if (min !== '' && max !== '' && Number(min) > Number(max)) {
      throw new Error(`${field.display_name} minimum cannot exceed maximum.`);
    }
    return {
      min: min === '' ? null : Number(min),
      max: max === '' ? null : Number(max),
    };
  }
  if (field.match_mode === 'allowed_values') {
    const allowedValues = Array.isArray(config.allowed_values) ? config.allowed_values.filter(Boolean) : [];
    if (!allowedValues.length) {
      throw new Error(`${field.display_name} needs at least one allowed value.`);
    }
    return { allowed_values: allowedValues };
  }
  if (field.match_mode === 'boolean_support') {
    const support = String(config.support || 'either').toLowerCase();
    if (!['yes', 'no', 'either'].includes(support)) {
      throw new Error(`${field.display_name} support must be yes, no, or either.`);
    }
    return { support };
  }
  const value = config.value ?? '';
  if (value === '') {
    throw new Error(`${field.display_name} needs a value.`);
  }
  return { value };
}

export function matchingProfileFromCriteria(criteria, sharedFields, preservedProfile = {}) {
  const fieldsByKey = new Map(eligibleMatchingFields(sharedFields).map((field) => [field.key, field]));
  const seen = new Set();
  const normalizedCriteria = (criteria || [])
    .filter((criterion) => String(criterion.field_key || '').trim())
    .map((criterion) => {
      const field = fieldsByKey.get(String(criterion.field_key || '').trim());
      if (!field) {
        throw new Error('Select a matchable Need Category field.');
      }
      if (seen.has(field.key)) {
        throw new Error('Each matching field can only be selected once.');
      }
      seen.add(field.key);
      return {
        field_key: field.key,
        match_mode: field.match_mode,
        config: normalizeCriterionConfig(criterion, field),
      };
    });

  const nextProfile = { ...normalizeObject(preservedProfile) };
  if (normalizedCriteria.length) {
    nextProfile.schema = 'amx.matching_profile.v1';
    nextProfile.criteria = normalizedCriteria;
  } else {
    delete nextProfile.criteria;
  }
  return nextProfile;
}

export function blankMatchingCriterion(sharedFields, existingCriteria = []) {
  const used = new Set((existingCriteria || []).map((criterion) => criterion.field_key).filter(Boolean));
  const field = eligibleMatchingFields(sharedFields).find((candidate) => !used.has(candidate.key));
  if (!field) {
    return null;
  }
  return {
    id: `match-${Date.now()}-${Math.random().toString(36).slice(2)}`,
    field_key: field.key,
    match_mode: field.match_mode,
    config: criterionConfigFromLegacyValue(field, field.match_mode === 'boolean_support' ? 'either' : ''),
  };
}

export function blankAdditionalInput() {
  return {
    id: `input-${Date.now()}-${Math.random().toString(36).slice(2)}`,
    key: '',
    display_name: '',
    description: '',
    type: 'text',
    requirement_stage: 'request',
    display_order: 0,
    options: '',
    unit: '',
    min: '',
    max: '',
    accepted_file_types: '',
    multiple: false,
    data_schema: {},
    physical_item_details: '',
    reference_target: '',
  };
}

export function blankOutput() {
  return {
    id: `output-${Date.now()}-${Math.random().toString(36).slice(2)}`,
    output_id: '',
    key: '',
    name: '',
    description: '',
    kind: 'file',
    semantic_type: '',
    required: true,
    data_schema: {},
    display_order: 0,
  };
}
